import React, { useContext, useState, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CustomerContext } from "../CashierHomepage";
import { CustomerIdContext } from "../../context/CustomerIdProvider";
import CashierApi from "../../utility/CashierApi";
import { AutoBillContext } from "../../context/AutoBillProvider";
import PaymentMethod from "./PaymentMethod";

function Payment({ currency }) {
  const { createData, updateData, fetchOnlyData } = CashierApi();
  const queryClient = useQueryClient();
  const { newCartData, setUpdateStatus, updateResumeStatus, draftId } =
    useContext(CustomerContext);
  const { selectedCustomerId } = useContext(CustomerIdContext);
  const { autoBill } = useContext(AutoBillContext);
  const [newBill, setNewBill] = useState({});
  const [orderData, setOrderData] = useState({});
  const [payMethodModal, setPayMethodModal] = useState(false);

  const [payMethodName, setPayMethodName] = useState("");

  // Calling the payment endpoint
  const { data: paymentType } = useQuery({
    queryKey: ["paymentType"],
    queryFn: () => fetchOnlyData("payment-methods"),
    retry: 1,
    keepPreviousData: true,
    staleTime: 20 * 60 * 1000,
    cacheTime: 20 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const { data: template } = useQuery({
    queryKey: ["template"],
    queryFn: () => fetchOnlyData("receipt-template"),
    retry: 1,
    keepPreviousData: true,
    staleTime: 30 * 60 * 1000,
    cacheTime: 60 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const mutation = useMutation({
    mutationFn: (data) => {
      return updateResumeStatus
        ? updateData(`orders/update-draft/`, draftId, data)
        : createData("orders/store", data);
    },
    retry: false,
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ["cart"] });
      generateAndPrintReceipt(response);
      setUpdateStatus(false);
    },
  });

  const generateAndPrintReceipt = (response) => {
    const generateReceiptContent = (label) => `
    <html>
    <head>
        <style>
            body { font-family: Arial, sans-serif; }
            .receipt-container { width: 300px; margin: 0 auto; border: 1px solid #000; padding: 10px; }
            .receipt-header, .receipt-footer { text-align: center; margin-bottom: 20px; }
            .receipt-items { margin-bottom: 20px; }
            .receipt-item { display: flex; justify-content: space-between; margin-bottom: 10px; }
            .receipt-label { text-align: center; margin-bottom: 10px; font-weight: semi-bold; }
            .receipt-info { text-align: center; margin-bottom: 10px; }
            .barcode { text-align: center; margin-top: 20px; }
            hr { border: 0; border-top: 1px dashed #000; margin: 10px 0; }
            .page-break { page-break-after: always; }
        </style>
    </head>
    <body>
  <div class="receipt-container">
    <div class="receipt-header">
      <h2>${template?.header_line_one || ""}</h2>
      <h2>${template?.header_line_two || ""}</h2>
      <h2>${template?.header_line_three || ""}</h2>
    </div>

    <div class="receipt-info">
      <p>${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}</p>
      <p>Cashier</p>
    </div>

    <hr />

    <div class="receipt-items">
      ${newCartData
        ?.map(
          (item) => `
        <div class="receipt-item">
          <span>${item?.product?.product_name} x ${item?.quantity}</span>
          <span>${currency}${Number(item?.total_price).toLocaleString(
            undefined,
            {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }
          )}</span>
        </div>
      `
        )
        .join("")}
    </div>

    <div class="receipt-footer">
      ${autoBill
        ?.map(
          (item) => `
        <div class="receipt-item">
          <span>${item?.bill_name}</span>
          <span>${currency}${Number(item?.auto_bill).toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}</span>
        </div>
      `
        )
        .join("")}
    </div>

    <div class="receipt-item">
      <span>Total</span>
      <span>
        ${currency}${Number(
      response?.data?.order?.total_price || 0
    ).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}
      </span>
    </div>

    <div class="receipt-item">
      <span>Payment method: ${response?.data?.order?.payment_type}</span>
    </div>

    <div class="receipt-label">
      <p >${response?.data?.loyaltyPointsData?.CustomerLoyaltyPoint}</p>
      <p>${response?.data?.loyaltyPointsData?.Discount}</p>
    </div>

    <hr />

    <div class="barcode">
      <img src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAgAAAAICAYAAAA6ee..." />
    </div>

    <div class="receipt-label">
      <h2>${template?.footer_line_one || ""}</h2>
      <h2>${template?.footer_line_two || ""}</h2>
      <h2>${template?.footer_line_three || ""}</h2>
    </div>
  </div>

  <div class="page-break"></div>
</body>

    </html>
`;

    const numCopies = Number(template?.number_of_pages) || 1;
    let allReceipts = "";

    for (let i = 0; i < numCopies; i++) {
      allReceipts += generateReceiptContent("Customer Copy");
    }

    // Open new print window
    const printWindow = window.open("", "_blank", "width=600,height=800");
    if (printWindow) {
      printWindow.document.write(`
        <html>
        <head>
            <title>Receipt</title>
            <style>
              .page-break { page-break-after: always; }
            </style>
        </head>
        <body>
            ${allReceipts}
        </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
      printWindow.close();
    } else {
      console.error("Pop-up blocked! Allow pop-ups to print receipts.");
    }
  };

  const handleSubmit = () => {
    // mutation.mutate(orderData); //comment later

    if (!paymentType || paymentType.length === 0) {
      mutation.mutate(orderData);
    } else {
      setPayMethodModal(true);
    }
  };

  const handlePaymentMethod = (name) => {
    setPayMethodName(name);
    const updatedOrderData = {
      ...orderData,
      method_name: name,
    };
    setOrderData(updatedOrderData);
    mutation.mutate(updatedOrderData);
    setPayMethodModal(false);
  };

  useEffect(() => {
    setNewBill(autoBill);

    const cartInfo = newCartData?.map((item) => {
      return {
        product_id: Number(item?.product_id),
        price: Number(item?.price),
        quantity: item?.quantity,
      };
    });

    // console.log("autobillNew", autoBill, newBill);

    // console.log("AutoBill before sending:", autoBill);
    // console.log("NewBill before sending:", newBill);
    // console.log("Order Data before sending:", orderData);

    setOrderData({
      customer_id: selectedCustomerId,
      status: "completed",
      ...(paymentType?.length > 0 && { method_name: payMethodName }),
      carts: cartInfo,
      auto_bills: autoBill,
    });
  }, [newCartData, selectedCustomerId, payMethodName, autoBill]);

  return (
    <>
      <button
        type="submit"
        //   disabled={loading}
        onClick={() => handleSubmit()}
        className="p-2 my-4 rounded-lg text-white w-full bg-gray-500 hover:bg-gray-600"
        // disabled={newCart?.data?.length < 1} THis should be uncommented on confirmation
      >
        Proceed to pay
      </button>
      {payMethodModal && (
        <PaymentMethod
          setPayMethodModal={setPayMethodModal}
          handlePaymentMethod={handlePaymentMethod}
          paymentType={paymentType}
        />
      )}
    </>
  );
}

export default Payment;
